import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { landscapeApi } from '../../api/landscape'
import { RefusedError } from '../../api/client'
import { useSpoke } from '../../hooks/useSpoke'
import type { LandscapeSystem } from '../../types/landscape'

// Add a goal to the firm's landscape: a title, an optional line of detail, and which systems it
// needs. The goal unlocks on the map when those systems are connected.
export function GoalDialog({ systems, onClose }: { systems: LandscapeSystem[]; onClose: () => void }) {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const [title, setTitle] = useState('')
  const [detail, setDetail] = useState('')
  const [deps, setDeps] = useState<string[]>([])
  const add = useMutation({
    mutationFn: () => landscapeApi.addGoal(spoke, title, detail, deps),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['landscape', spoke] })
      onClose()
    },
  })
  const toggle = (id: string) => setDeps((d) => (d.includes(id) ? d.filter((x) => x !== id) : [...d, id]))

  function submit(e: FormEvent) {
    e.preventDefault()
    if (title.trim()) add.mutate()
  }

  return (
    <div className="dialog-backdrop" onClick={onClose} role="presentation">
      <form className="dialog" onClick={(e) => e.stopPropagation()} onSubmit={submit} aria-label="Add a goal">
        <h2>Add a goal</h2>
        <p className="muted">What should Scout deliver for the firm? Name it the way you would say it in the Wednesday call.</p>
        <label>
          Goal
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Three new private-credit mandates this year" autoFocus required />
        </label>
        <label>
          Detail (optional)
          <input value={detail} onChange={(e) => setDetail(e.target.value)} maxLength={300} placeholder="How we will know it is done" />
        </label>
        <fieldset className="deps">
          <legend>Needs</legend>
          {systems.map((s) => (
            <label key={s.id} className="check">
              <input type="checkbox" checked={deps.includes(s.id)} onChange={() => toggle(s.id)} />
              <span>{s.name}</span>
              <span className="muted small">{s.status === 'connected' ? 'connected' : s.status === 'available' ? 'available' : 'coming'}</span>
            </label>
          ))}
        </fieldset>
        {add.isError && <p className="error">{add.error instanceof RefusedError ? add.error.refusal.message : add.error.message}</p>}
        <div className="actions">
          <button type="submit" className="button primary" disabled={!title.trim() || add.isPending}>
            {add.isPending ? 'Adding…' : 'Add goal'}
          </button>
          <button type="button" className="button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}
